import React, {useState} from 'react';

import {CollapsibleItemStructure} from 'app/general/structures/CollapsibleItemStructure';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt, ngt} from 'app/utils/i18n';

import {TeamSignoffsList} from './TeamSignoffsList';

import type {
  GroupedSignoffSerializedType,
  TeamSerializedType,
} from 'sweet-types';

interface Props {
  object: GroupedSignoffSerializedType;
  team: TeamSerializedType;
  ordering: string;
}
export const TeamGroupedSignoffsListItem = ({
  object,
  team,
  ordering = '',
}: Props) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const ct = getContentType(object.grouped_by_contenttype);

  // pick the main ordering field only
  const firstOrderingField = ordering
    .split(',')[0]
    .replace('last_signoff_at', 'signed_at')
    // we don't extend the search to subitems
    .replace('-rank', '');

  const preparedOrdering = [
    // always show signed-off docs/users first
    'is_signed_off',
    // then use the parent ordering
    firstOrderingField,
    // and fallback to alphabetical (e.g. when previous ordering field is all null)
    object.group_type === 'doc' ? 'user__first_name' : 'name',
  ]
    .filter((f) => f != '')
    .join(',');

  return (
    <CollapsibleItemStructure
      icon={<i className={ct.icon} />}
      title={
        <div className="flex-grow-1 row pl-0">
          <div className="text-dark font-weight-bold d-block d-sm-inline col">
            {getObjectName(object.name, object.grouped_by_contenttype)}
          </div>
          <div className="col-auto">
            <div className="d-inline text-muted font-italic">
              {object.signoff_pending === 0
                ? gt('No pending sign offs')
                : ngt(
                    '%d pending sign off',
                    '%d pending sign offs',
                    object.signoff_pending
                  )}
            </div>
          </div>
        </div>
      }
      expanded={expanded}
      onToggleExpand={() => setExpanded(!expanded)}
      childrenClass="border-bottom"
    >
      <TeamSignoffsList
        paramsBuilder={() => ({
          team_id: team.id,
          ordering: preparedOrdering,
          ...(object.group_type === 'user'
            ? {
                user_id: object.grouped_by_object_id,
              }
            : object.group_type === 'doc'
            ? {
                object_id: object.grouped_by_object_id,
                content_type: object.grouped_by_contenttype,
              }
            : {}),
        })}
        noItemsMessage={() => gt('No sign offs')}
        pageParamName={`${object.group_type}_${object.grouped_by_object_id}_page`}
        groupedBy={object.group_type}
        team={team}
        loadingListProps={{
          small: true,
          lines: object.signoff_total,
        }}
      />
    </CollapsibleItemStructure>
  );
};
